import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Save, Pencil, FileText, Building2, Check } from "lucide-react";

const empty = {
  company_type: "individual",
  company_name: "",
  siret: "",
  vat_number: "",
  billing_address: "",
  billing_postal_code: "",
  billing_city: "",
  billing_country: "France",
};

export default function BillingInfoCard({ user, onSaved }) {
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      const merged = {
        company_type: user.company_type || "individual",
        company_name: user.company_name || "",
        siret: user.siret || "",
        vat_number: user.vat_number || "",
        billing_address: user.billing_address || "",
        billing_postal_code: user.billing_postal_code || "",
        billing_city: user.billing_city || "",
        billing_country: user.billing_country || "France",
      };
      setForm(merged);
      // Édition par défaut si rien n'est rempli
      setEditing(!user.billing_address);
    }
  }, [user]);

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    const isCompany = form.company_type === "company";
    if (isCompany && form.siret) {
      const digits = form.siret.replace(/\D/g, "");
      if (digits.length !== 14) {
        toast.error("Le SIRET doit contenir 14 chiffres");
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", {
        company_type: form.company_type,
        company_name: form.company_name || null,
        siret: form.siret || null,
        vat_number: form.vat_number || null,
        billing_address: form.billing_address || null,
        billing_postal_code: form.billing_postal_code || null,
        billing_city: form.billing_city || null,
        billing_country: form.billing_country || null,
      });
      toast.success("Informations de facturation enregistrées");
      setEditing(false);
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const hasInfo = user?.billing_address || user?.siret;
  const isCompany = form.company_type === "company";

  if (!editing) {
    return (
      <div className="mt-6 border border-[#1F4032]/10 bg-white p-5 rounded-md" data-testid="billing-info-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-[#1F4032] mt-1" />
            <div>
              <div className="overline text-accent">— Facturation</div>
              <div className="font-display font-bold text-lg tracking-tight">Mes informations légales</div>
              {hasInfo ? (
                <div className="text-sm text-neutral-600 mt-2 font-mono space-y-1">
                  {user.company_name && <div><strong>{user.company_name}</strong></div>}
                  {user.siret && <div>SIRET : {user.siret}</div>}
                  {user.vat_number && <div>TVA : {user.vat_number}</div>}
                  {user.billing_address && (
                    <div className="text-neutral-500">
                      {user.billing_address}<br />
                      {user.billing_postal_code} {user.billing_city}
                      {user.billing_country && `, ${user.billing_country}`}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-neutral-500 mt-2">
                  Aucune information de facturation renseignée.
                </div>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            className="rounded-md border-[#1F4032] gap-2"
            data-testid="edit-billing-btn"
          >
            <Pencil className="w-3.5 h-3.5" /> Modifier
          </Button>
        </div>
        {!hasInfo && (
          <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded">
            ⚠️ Renseignez ces informations pour que vos factures PDF soient conformes (mentions obligatoires).
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-6 border border-[#1F4032] bg-white p-5 rounded-md" data-testid="billing-info-card-editing">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="w-5 h-5 text-[#1F4032]" />
        <div className="font-display font-bold text-lg tracking-tight">Informations de facturation</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Type de compte</Label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, company_type: "individual" })}
              className={`flex-1 px-4 py-3 border rounded-md text-sm text-left transition-colors ${
                form.company_type === "individual"
                  ? "border-[#1F4032] bg-[#1F4032]/5"
                  : "border-neutral-200 hover:border-[#1F4032]/40"
              }`}
              data-testid="type-individual-btn"
            >
              <div className="font-bold flex items-center gap-2">
                {form.company_type === "individual" && <Check className="w-4 h-4 text-[#1F4032]" />}
                Particulier
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">Sans SIRET</div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, company_type: "company" })}
              className={`flex-1 px-4 py-3 border rounded-md text-sm text-left transition-colors ${
                form.company_type === "company"
                  ? "border-[#1F4032] bg-[#1F4032]/5"
                  : "border-neutral-200 hover:border-[#1F4032]/40"
              }`}
              data-testid="type-company-btn"
            >
              <div className="font-bold flex items-center gap-2">
                {form.company_type === "company" && <Check className="w-4 h-4 text-[#1F4032]" />}
                Entreprise / Auto-entrepreneur
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">Avec SIRET (recommandé)</div>
            </button>
          </div>
        </div>

        {isCompany && (
          <>
            <div>
              <Label className="mb-1.5 block text-xs">Raison sociale *</Label>
              <Input
                value={form.company_name}
                onChange={up("company_name")}
                placeholder="Mon Entreprise SARL"
                data-testid="billing-company-name"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs">SIRET (14 chiffres) *</Label>
              <Input
                value={form.siret}
                onChange={up("siret")}
                placeholder="12345678901234"
                maxLength={20}
                data-testid="billing-siret"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block text-xs">N° TVA intracommunautaire (facultatif)</Label>
              <Input
                value={form.vat_number}
                onChange={up("vat_number")}
                placeholder="FR12345678901"
                data-testid="billing-vat"
              />
            </div>
          </>
        )}

        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Adresse de facturation *</Label>
          <Input
            value={form.billing_address}
            onChange={up("billing_address")}
            placeholder="12 rue de la République"
            data-testid="billing-address"
          />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Code postal *</Label>
          <Input
            value={form.billing_postal_code}
            onChange={up("billing_postal_code")}
            placeholder="75001"
            data-testid="billing-postal-code"
          />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Ville *</Label>
          <Input
            value={form.billing_city}
            onChange={up("billing_city")}
            placeholder="Paris"
            data-testid="billing-city"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="mb-1.5 block text-xs">Pays</Label>
          <Input
            value={form.billing_country}
            onChange={up("billing_country")}
            placeholder="France"
            data-testid="billing-country"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        {hasInfo && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setEditing(false)}
            className="rounded-md"
            data-testid="cancel-billing-btn"
          >
            Annuler
          </Button>
        )}
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-md bg-[#1F4032] hover:bg-[#2A5443] gap-2"
          data-testid="save-billing-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
